// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Pure decision logic for the tile ECG heartbeat (HeartbeatECG component).
 *
 * A "beat" fires when a device's activity signal (an ISO timestamp string
 * from MqttActivityContext for stargates, or `last_seen` from the poll for
 * edge devices) CHANGES while the device is considered reachable. The
 * component guards the mount case by initialising its previous-signal ref to
 * the first observed value — so shouldPulse never needs a first-render
 * sentinel; a null→value transition seen here is a genuine mid-session first
 * message and DOES pulse.
 */

/**
 * Minimum spacing between visual beats. Stargates publish in bursts (status
 * heartbeat + health topics + metrics land within ~a second each cycle);
 * collapsing anything faster than this into one beat keeps the ECG a clean
 * once-per-check-in pulse instead of a flicker.
 */
export const BEAT_MIN_INTERVAL_MS = 1500;

/**
 * Should a beat fire for this signal transition?
 *  - never when the device isn't reachable (`active` false)
 *  - never when there is no signal at all
 *  - only on a real VALUE change (the 30s poll rebuilds objects with
 *    identical values — identity changes must not pulse)
 *  - MONOTONIC: when both values parse as timestamps, only a strictly LATER
 *    one beats. Activity timestamps never legitimately move backwards, so
 *    this suppresses phantom beats from source regressions (activityMap
 *    pruning/clearing falls back to the older poll `last_seen`) and makes a
 *    same-instant format switch (poll "+00:00" vs live "Z") a non-event.
 */
export function shouldPulse({ prevSignal, signal, active }) {
  if (!active) return false;
  if (signal == null || signal === '') return false;
  if (prevSignal == null || prevSignal === '') return true; // first message while mounted
  if (prevSignal === signal) return false;
  const prevT = Date.parse(prevSignal);
  const nextT = Date.parse(signal);
  if (Number.isFinite(prevT) && Number.isFinite(nextT)) {
    return nextT > prevT;
  }
  return true; // unparseable values: plain change detection
}

/**
 * What the component's previous-signal ref should become after a render.
 * FROZEN while inactive: a recovering stargate's live message always lands
 * BEFORE the 30s poll flips health back to reachable — advancing the ref on
 * that render would swallow the recovery beat (prev === signal by the time
 * `active` turns true). Freezing means activation fires one honest beat for
 * everything that happened during the outage.
 */
export function nextPrevSignal({ prevSignal, signal, active }) {
  return active ? signal : prevSignal;
}
